import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import { useNavigate } from '../../hooks/useNavigate'
import useParamId from '../../hooks/useParamId'
import { useUserInfo } from '../../hooks/useUserInfo'

import { getUserProductSell } from '../../api/product'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, CenterWrap, ColumnItem } from '../../components/common'
import { ProductInfoCard } from '../../components/card/ProductInfoCard'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { RowData } from './components'

import { Color } from '../../theme/color'

import { ReactComponent as ArrowRightGrayIcon } from '../../assets/ico/common/arrow/right/arrow_right_gray_18.svg'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'

export function SaleInfo() {
  const {
    commonStore: { setInvalidAccess },
  } = useContext(StoresContext)

  const navigate = useNavigate()
  const userProductId = useParamId(3)
  const { isLogin, isCheckingLogin } = useUserInfo()

  const {
    data: userProductSell,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: [QUERY_KEY.userProductSell, { userProductId }],
    queryFn: async () => await getUserProductSell(userProductId as number),
    enabled: typeof userProductId === 'number',
  })

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (isError && error) {
      if (isAxiosError(error)) {
        toast(error.response?.data.message, error)
      }
    }
  }, [isError, error])

  if (isPending || isError) {
    return (
      <Wrap>
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </Wrap>
    )
  }

  const goProductDetail = () => {
    navigate(`/market/product/${userProductSell.product.id}`)
  }

  const isFirstSellProducts = [25, 61].includes(userProductSell.productId)

  return (
    <Wrap>
      <Header type="SUB" title="매각 정보" />
      <Content>
        <ProductInfoWrap>
          <ProductInfoCard>
            <ProductInfoContent $gap={16}>
              <ProductImage>
                <img src={userProductSell.product.imageUri} alt={userProductSell.product.name} />
              </ProductImage>
              <ColumnItem $gap={4}>
                <Text size={18} weight={600}>
                  {userProductSell.product.artist}
                </Text>
                <Text size={14} color="text_secondary">
                  {userProductSell.product.name}
                </Text>
              </ColumnItem>
              <ProductDetailLink onClick={goProductDetail}>
                <Text size={14} weight={500} color="text_secondary">
                  작품 상세 보기
                </Text>
                <ArrowRightGrayIcon />
              </ProductDetailLink>
            </ProductInfoContent>
            <DivideLine />
            <RowData
              title="총 배정수량"
              content={`${(
                userProductSell.companyAllotmentShares + userProductSell.commonAllotmentShares
              ).toLocaleString()}주`}
            />
            <RowData
              isChildren={true}
              title={{ value: '회사 배정', size: 16 }}
              content={{
                value: `${userProductSell.companyAllotmentShares.toLocaleString()}주`,
                size: 16,
              }}
            />
            <RowData
              isChildren={true}
              title={{ value: '일반 배정', size: 16 }}
              content={{
                value: `${userProductSell.commonAllotmentShares.toLocaleString()}주`,
                size: 16,
              }}
            />
            <RowData title="모집총액" content={`${userProductSell.totalIssuedPrice.toLocaleString()}원`} />
            <RowData title="모집가액" content={`${userProductSell.offerPrice.toLocaleString()}원`} />
            <RowData title="배정공고일" content={`${dayjs(userProductSell.allotmentDate).format('YYYY-MM-DD')}`} />
            <RowData
              title={isFirstSellProducts ? '예상 청산금' : '예상 매각대금'}
              content={`${userProductSell.sellPrice.toLocaleString()}원`}
            />
            <DivideLine />
            <Text size={18} weight={600}>
              예상 수익
            </Text>
            <RowData title="배정금액" content={`${userProductSell.originalBalance.toLocaleString()}원`} />
            <RowData title="배정수량" content={`${userProductSell.totalShares.toLocaleString()}주`} />
            <RowData
              title="세전수익률"
              content={`${userProductSell.revenueRate >= 0.0 ? '+' : '-'}` + `${userProductSell.revenueRate}%`}
            />
            <RowData
              title="세전수익금"
              content={`${(userProductSell.userRevenuePrice - userProductSell.originalBalance).toLocaleString()}원`}
            />
            <RowData title="예상 정산금" content={`${userProductSell.userRevenuePrice.toLocaleString()}원`} />
          </ProductInfoCard>
        </ProductInfoWrap>
        <NoticeContainer
          title="안내사항"
          list={[
            '투자계약서 내에 명시된 배당소득세 및 판매수수료, 성과보수 등의 비용을 차감 시, 실제 수익률은 달라질 수 있어요.',
            '작품 매각이 완료되면 정산 절차가 진행될 예정이에요. 추후 매각 대금 정산을 위한 절차 진행 부탁드려요.',
          ]}
          $backgroundColor="gray_1"
        />
      </Content>
    </Wrap>
  )
}

const ProductInfoWrap = styled.div`
  padding: 16px;
  background-color: ${Color.gray_1};
`

const ProductInfoContent = styled(AlignCenterItem)`
  position: relative;
`

const ProductImage = styled.div`
  overflow: hidden;
  width: 100px;
  aspect-ratio: 1/1.3;
  border-radius: 8px;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ProductDetailLink = styled(AlignCenterItem)`
  position: absolute;
  right: 0;
  bottom: 0;
  border: none;
  outline: none;
  cursor: pointer;
`
